import React from 'react';
import { AlertTriangle, Phone } from 'lucide-react';
import { SITE } from '../../seo/siteMeta';

/**
 * Sitewide emergency announcement ticker.
 *
 * Pinned to the very top of the viewport, above the main header. Renders the
 * announcement text as a horizontally scrolling marquee (right-to-left, the
 * conventional news-ticker direction) and respects prefers-reduced-motion by
 * falling back to a centered static line.
 *
 * Implementation notes:
 *   - We render the text TWICE inside the moving track so that as the first
 *     copy scrolls off the left edge, the second copy is already filling the
 *     right edge — making the loop visually seamless.
 *   - The track animates from `translateX(0)` to `translateX(-50%)`, which is
 *     exactly the width of one copy because two copies sit side-by-side.
 *   - Phone number remains a tel: link so click-to-call works on mobile.
 */

const ANNOUNCEMENT = '24/7 Emergency Callouts — Burst pipes, leaks, no heat. We answer day or night.';

const Marquee = () => (
  <div className="harp-marquee group" aria-label="Emergency announcement">
    <div className="harp-marquee__track">
      {/* Render the same content twice for a seamless loop */}
      {[0, 1].map((dup) => (
        <div
          key={dup}
          className="harp-marquee__copy"
          aria-hidden={dup === 1 ? 'true' : 'false'}
        >
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span className="font-semibold tracking-wide whitespace-nowrap">{ANNOUNCEMENT}</span>
          <span className="whitespace-nowrap">Call</span>
          <a
            href={`tel:${SITE.phoneTel}`}
            className="inline-flex items-center gap-1 underline underline-offset-2 hover:text-amber-200 font-semibold whitespace-nowrap"
            data-testid={dup === 0 ? 'emergency-banner-call' : undefined}
          >
            <Phone className="w-3.5 h-3.5" /> {SITE.phone}
          </a>
          {/* Separator between repeats */}
          <span className="harp-marquee__sep" aria-hidden="true">•</span>
        </div>
      ))}
    </div>
  </div>
);

const EmergencyBanner = ({ hideOn = [] }) => {
  if (typeof window !== 'undefined' && hideOn.some((p) => window.location.pathname.startsWith(p))) {
    return null;
  }
  return (
    <div
      className="harp-emergency-banner"
      role="region"
      aria-label="Emergency contact information"
      data-testid="emergency-banner"
    >
      <Marquee />
    </div>
  );
};

export default EmergencyBanner;
